import React, { useCallback } from 'react';

import styled from 'styled-components';

import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { customField, textField } from '@cd3p/core/utils/fields';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormField,
  Grid,
  IconButton,
  Paper,
  Stack,
} from 'components';

import { CustomerMultiselectField } from './CustomerMultiselectField';

import { FieldError, useFieldArray, useFormContext } from 'third-party';
import { useTranslation } from 'third-party';

import { TypeaheadDataOption } from 'types/app';

const StyledPaper = styled(Paper)`
  padding: ${props => props.theme.spacing(2)};
  margin-bottom: ${props => props.theme.spacing(2)};
  position: relative;
  background-color: ${props => props.theme.palette.background.paper};
`;

const DeleteButton = styled(IconButton)`
  position: absolute;
  top: 8;
  right: 8;
  color: ${props => props.theme.palette.error.main};
  &:hover {
    background-color: ${props => props.theme.palette.error.light};
  }
`;

const AddFieldButton = styled(Button)`
  margin-top: ${props => props.theme.spacing(2)};
  border-width: 2;
  align-self: flex-start;
`;

const FieldWrapper = styled(Box)`
  margin-top: ${props => props.theme.spacing(2)};
`;

export enum CustomOrderFieldFormFields {
  Label = 'label',
  Placeholder = 'placeholder',
  Customers = 'customers',
  ApplyToAllCustomers = 'applyToAllCustomers',
}

export type CustomOrderCustomersFormT = {
  id: string;
  name: string;
};

export type CustomOrderFieldFormT = {
  [CustomOrderFieldFormFields.Label]: string;
  [CustomOrderFieldFormFields.Placeholder]: string;
  [CustomOrderFieldFormFields.Customers]:
    | TypeaheadDataOption<CustomOrderCustomersFormT>[]
    | null;
  [CustomOrderFieldFormFields.ApplyToAllCustomers]: boolean;
};

const formDefaultValues: CustomOrderFieldFormT = {
  [CustomOrderFieldFormFields.Label]: '',
  [CustomOrderFieldFormFields.Placeholder]: '',
  [CustomOrderFieldFormFields.Customers]: null,
  [CustomOrderFieldFormFields.ApplyToAllCustomers]: true,
};

export const CUSTOM_ORDER_FIELDS_FORM_KEY = 'customOrderFields';

export const OrderFieldsSchemaForm: React.FC = () => {
  const { t } = useTranslation();

  const {
    control,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<{
    [CUSTOM_ORDER_FIELDS_FORM_KEY]: CustomOrderFieldFormT[];
  }>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: CUSTOM_ORDER_FIELDS_FORM_KEY,
  });

  const onAddCustomField = useCallback(() => {
    append(formDefaultValues);
  }, [append]);

  const onDeleteCustomField = useCallback(
    (index: number) => () => {
      remove(index);
    },
    [remove],
  );

  return (
    <Stack spacing={3}>
      {fields.map((field, index) => {
        const customersFieldName = `${CUSTOM_ORDER_FIELDS_FORM_KEY}.${index}.${CustomOrderFieldFormFields.Customers}`;

        const applyToAllCustomersWatch = watch(
          `${CUSTOM_ORDER_FIELDS_FORM_KEY}.${index}.${CustomOrderFieldFormFields.ApplyToAllCustomers}`,
        );

        return (
          <FieldWrapper key={field.id}>
            <StyledPaper variant="outlined">
              <Grid container spacing={2} sx={{ pr: 5 }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormField
                    fieldName={`${CUSTOM_ORDER_FIELDS_FORM_KEY}.${index}.${CustomOrderFieldFormFields.Label}`}
                    label={t('settings.orders.customFields.fieldLabel')}
                    placeholder={t(
                      'settings.orders.customFields.fieldLabelPlaceholder',
                    )}
                    control={control}
                    isRequired
                    requiredErrorMessage={t('common.form.error.emptyField')}
                    fieldError={
                      errors[CUSTOM_ORDER_FIELDS_FORM_KEY]?.[index]?.[
                        CustomOrderFieldFormFields.Label
                      ]
                    }
                    maxLength={customField.maxLength}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormField
                    fieldName={`${CUSTOM_ORDER_FIELDS_FORM_KEY}.${index}.${CustomOrderFieldFormFields.Placeholder}`}
                    label={t('settings.orders.customFields.fieldPlaceholder')}
                    placeholder={t(
                      'settings.orders.customFields.fieldPlaceholderPlaceholder',
                    )}
                    isRequired
                    requiredErrorMessage={t('common.form.error.emptyField')}
                    control={control}
                    fieldError={
                      errors[CUSTOM_ORDER_FIELDS_FORM_KEY]?.[index]?.[
                        CustomOrderFieldFormFields.Placeholder
                      ]
                    }
                    maxLength={textField.maxLength}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <FormField
                    fieldName={`${CUSTOM_ORDER_FIELDS_FORM_KEY}.${index}.${CustomOrderFieldFormFields.ApplyToAllCustomers}`}
                    control={control}
                    isRequired={false}
                    render={({ field: { value, onChange } }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={Boolean(value)}
                            onChange={e => {
                              onChange(e.target.checked);
                              if (e.target.checked) {
                                // @ts-ignore
                                setValue(customersFieldName, null, {
                                  shouldValidate: true,
                                });
                              }
                            }}
                          />
                        }
                        label={t(
                          'settings.orders.customFields.applyToAllCustomers',
                        )}
                      />
                    )}
                  />
                  {!applyToAllCustomersWatch && (
                    <Box mt={2}>
                      <CustomerMultiselectField
                        fieldName={customersFieldName}
                        label={t('settings.orders.customFields.customersLabel')}
                        placeholder={t(
                          'settings.orders.customFields.customersPlaceholder',
                        )}
                        requiredErrorMessage={t('common.form.error.emptyField')}
                        fieldError={
                          errors[CUSTOM_ORDER_FIELDS_FORM_KEY]?.[index]?.[
                            CustomOrderFieldFormFields.Customers
                          ] as FieldError
                        }
                      />
                    </Box>
                  )}
                </Grid>
              </Grid>
              <DeleteButton
                onClick={onDeleteCustomField(index)}
                aria-label={t('common.delete')}
                size="small"
              >
                <DeleteOutlineIcon fontSize="small" />
              </DeleteButton>
            </StyledPaper>
          </FieldWrapper>
        );
      })}

      <AddFieldButton
        onClick={onAddCustomField}
        variant="outlined"
        color="secondary"
        startIcon={<AddBoxOutlinedIcon />}
      >
        {t('settings.orders.customFields.addButton')}
      </AddFieldButton>
    </Stack>
  );
};
